import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Autocomplete,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { ticketService, CreateTicketRequest } from '@shared/services/TicketService'
import { categoryService } from '@shared/services/CategoryService'
import { attachmentService } from '@shared/services/AttachmentService'
import { OrganizationService } from '@shared/services/OrganizationService'
import { CategorySummary, TicketPriority, Organization, User } from '@shared/types/entities'
import { apiClient } from '@shared/services/ApiClient'

interface RequesterInfo {
  userId?: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
}

function AgentCreateTicket() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [categories, setCategories] = useState<CategorySummary[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [requesterInfo, setRequesterInfo] = useState<RequesterInfo>({
    email: '',
    firstName: '',
    lastName: ''
  })
  const [isNewRequester, setIsNewRequester] = useState(false)

  const [formData, setFormData] = useState<CreateTicketRequest>({
    subject: '',
    description: '',
    priority: 'Medium',
    categoryId: undefined,
    source: 'Phone',
    isPrivate: false,
    organisationId: undefined,
    childSiteId: undefined
  })

  useEffect(() => {
    loadCategories()
    loadOrganizations()
    loadUsers()
  }, [])

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories()
      setCategories(data.filter(c => c.isActive))
    } catch (err: any) {
      console.error('Failed to load categories:', err)
    } finally {
      setLoadingCategories(false)
    }
  }

  const loadOrganizations = async () => {
    try {
      setLoadingOrganizations(true)
      const data = await OrganizationService.getOrganizations()
      // Show all organizations and child sites (don't filter by isActive - allow tickets for all levels)
      setOrganizations(data)
    } catch (err: any) {
      console.error('Failed to load organizations:', err)
    } finally {
      setLoadingOrganizations(false)
    }
  }

  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      // Get all users (agents can create tickets for any customer)
      const response = await apiClient.get<User[]>('/users')
      // Filter to only customers
      setUsers(response.filter(u => u.role === 'Customer'))
    } catch (err: any) {
      console.error('Failed to load users:', err)
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleRequesterSelect = (selectedUser: User | null) => {
    if (selectedUser) {
      setRequesterInfo({
        userId: selectedUser.userId,
        email: selectedUser.email,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        phoneNumber: selectedUser.phoneNumber || undefined
      })
      setIsNewRequester(false)
    } else {
      // Clear selection
      setRequesterInfo({
        email: '',
        firstName: '',
        lastName: ''
      })
      setIsNewRequester(true)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newFiles = Array.from(files)
    setSelectedFiles(prev => [...prev, ...newFiles])

    // Reset input so the same file can be selected again if needed
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Subject and description are required')
      return
    }

    if (!requesterInfo.email || !requesterInfo.firstName) {
      setError('Please select or enter requester information')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // If new requester, create user first
      let requesterId = requesterInfo.userId
      if (isNewRequester || !requesterId) {
        try {
          const newUser = await apiClient.post<User>('/users', {
            email: requesterInfo.email,
            firstName: requesterInfo.firstName,
            lastName: requesterInfo.lastName,
            phoneNumber: requesterInfo.phoneNumber,
            role: 'Customer',
            organisationId: formData.organisationId,
            childSiteId: formData.childSiteId
          })
          requesterId = newUser.userId
        } catch (userErr: any) {
          setError(`Failed to create requester: ${userErr.message}`)
          return
        }
      }

      // Create the ticket with requesterId
      const ticketRequest = {
        ...formData,
        requesterId
      }

      const ticket = await ticketService.createTicket(ticketRequest)

      // Upload attachments if any
      if (selectedFiles.length > 0) {
        setUploadingFiles(true)
        try {
          await Promise.all(
            selectedFiles.map(file =>
              attachmentService.uploadAttachment(file, ticket.ticketId)
            )
          )
        } catch (uploadErr: any) {
          console.error('Failed to upload some attachments:', uploadErr)
        } finally {
          setUploadingFiles(false)
        }
      }

      navigate(`/agent/tickets/${ticket.ticketId}`)
    } catch (err: any) {
      setError(err.message || 'Failed to create ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof CreateTicketRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const priorities: TicketPriority[] = ['Low', 'Medium', 'High', 'Urgent']
  const sources = ['Phone', 'Walk-in', 'Email', 'Portal']

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/agent/tickets')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">Create New Ticket</Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Requester Selection */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              Requester Information
            </Typography>

            <Autocomplete
              options={users}
              loading={loadingUsers}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              onChange={(_, value) => handleRequesterSelect(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Existing Customer"
                  placeholder="Search by name or email..."
                  helperText="Select an existing customer or enter new requester details below"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 3 }}
            />

            {/* Manual Requester Entry */}
            {(isNewRequester || !requesterInfo.userId) && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Requester Email"
                    required
                    type="email"
                    value={requesterInfo.email}
                    onChange={(e) => setRequesterInfo(prev => ({ ...prev, email: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Requester Phone (Optional)"
                    value={requesterInfo.phoneNumber || ''}
                    onChange={(e) => setRequesterInfo(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    value={requesterInfo.firstName}
                    onChange={(e) => setRequesterInfo(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={requesterInfo.lastName}
                    onChange={(e) => setRequesterInfo(prev => ({ ...prev, lastName: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            )}

            {/* Ticket Details */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Ticket Details
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={formData.source}
                label="Source"
                onChange={(e) => handleChange('source', e.target.value)}
                disabled={loading}
              >
                {sources.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Organisation / Site</InputLabel>
              <Select
                value={formData.childSiteId ? `site-${formData.childSiteId}` : formData.organisationId ? `org-${formData.organisationId}` : ''}
                label="Organisation / Site"
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) {
                    // None selected - tenant-level ticket
                    setFormData(prev => ({ ...prev, organisationId: undefined, childSiteId: undefined }))
                  } else if (value.startsWith('org-')) {
                    // Organisation selected
                    setFormData(prev => ({ ...prev, organisationId: value.substring(4), childSiteId: undefined }))
                  } else if (value.startsWith('site-')) {
                    // Child site selected
                    setFormData(prev => ({ ...prev, organisationId: undefined, childSiteId: value.substring(5) }))
                  }
                }}
                disabled={loading || loadingOrganizations}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {organizations.map((org) => (
                  <MenuItem
                    key={org.organizationId}
                    value={org.childSiteId ? `site-${org.childSiteId}` : `org-${org.organizationId}`}
                  >
                    {org.name} {org.type && `(${org.type})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Subject"
              required
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Brief summary of the issue"
            />

            <TextField
              fullWidth
              label="Description"
              required
              multiline
              rows={6}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Detailed description of the issue"
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => handleChange('priority', e.target.value as TicketPriority)}
                    disabled={loading}
                  >
                    {priorities.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category (Optional)</InputLabel>
                  <Select
                    value={formData.categoryId || ''}
                    label="Category (Optional)"
                    onChange={(e) => handleChange('categoryId', e.target.value || undefined)}
                    disabled={loading || loadingCategories}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Tags (Optional)"
              value={formData.tags || ''}
              onChange={(e) => handleChange('tags', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Comma-separated tags (e.g., login, password, urgent)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isGroupTicket || false}
                  onChange={(e) => handleChange('isGroupTicket', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Group Ticket (all users in the same site can view and collaborate)"
              sx={{ mb: 3 }}
            />

            {/* File Attachments Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Attachments (Optional)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Attach files, images, or take photos to help describe the issue
              </Typography>

              <Stack direction="row" spacing={1} mb={2}>
                {/* Regular file upload button */}
                <input
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx,.txt,.xlsx,.xls"
                  disabled={loading}
                />
                <label htmlFor="file-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                    disabled={loading}
                  >
                    Choose Files
                  </Button>
                </label>

                {/* Camera capture button for mobile */}
                <input
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="camera-capture-input"
                  accept="image/*"
                  capture="environment"
                  disabled={loading}
                />
                <label htmlFor="camera-capture-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CameraAltIcon />}
                    disabled={loading}
                  >
                    Take Photo
                  </Button>
                </label>
              </Stack>

              {/* Selected files preview */}
              {selectedFiles.length > 0 && (
                <Stack spacing={1}>
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<CloseIcon />}
                      variant="outlined"
                      sx={{ justifyContent: 'space-between', maxWidth: '100%' }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/agent/tickets')}
                disabled={loading || uploadingFiles}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading || uploadingFiles ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={loading || uploadingFiles}
              >
                {uploadingFiles ? 'Uploading files...' : loading ? 'Creating...' : 'Create Ticket'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default AgentCreateTicket
